import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getJobs,
  getProjects,
  getEvents,
  getGroups,
  getGroupPosts,
  getArticles
} from '../services/firestoreService';
import { Link } from 'react-router-dom';
import ProfileCompletionBanner from '../components/profile/ProfileCompletionBanner';
import { toJsDate } from '../utils/formatDate';

export default function Dashboard() {
  const { currentUser, userProfile, membership, isActiveMember, hasRole } = useAuth();
  const navigate = useNavigate();
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [, setRecentGroups] = useState([]); // Groups data for future use
  const [groupsWithActivity, setGroupsWithActivity] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is admin
  const isAdmin = hasRole('admin');

  // Profile completion is now a soft, dismissible banner (ProfileCompletionBanner)
  // shown below, instead of a hard redirect that trapped members on the wizard.

  useEffect(() => {
    async function fetchDashboardData() {
      try {
        setLoading(true);
        setError(null);

        console.log('Fetching dashboard data...');

        // Fetch basic data
        const [jobs, projects, events] = await Promise.all([
          getJobs(5).catch(err => {
            console.error('Error fetching jobs:', err);
            return [];
          }),
          getProjects(5).catch(err => {
            console.error('Error fetching projects:', err);
            return [];
          }),
          getEvents(5, true).catch(err => {
            console.error('Error fetching events:', err);
            return [];
          })
        ]);

        console.log('Fetched data:', { jobs, projects, events });

        setRecentJobs(jobs);
        setRecentProjects(projects);
        setUpcomingEvents(events);

        // Fetch additional data for admin or members
        if (isAdmin || isActiveMember()) {
          try {
            const [groups, articles] = await Promise.all([
              getGroups(10).catch(err => {
                console.error('Error fetching groups:', err);
                return [];
              }),
              getArticles(5, null, isAdmin).catch(err => {
                console.error('Error fetching articles:', err);
                return [];
              })
            ]);

            console.log('Fetched additional data:', { groups, articles });
            setRecentGroups(groups);
            setRecentArticles(articles);

            // For admin, check for groups with recent activity
            if (isAdmin && groups.length > 0) {
              const groupsActivity = await Promise.all(
                groups.slice(0, 5).map(async (group) => {
                  try {
                    const posts = await getGroupPosts(group.id);
                    const recentPosts = posts.filter(post => {
                      const postDate = post.createdAt?.toDate?.() || new Date(0);
                      const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
                      return postDate > dayAgo;
                    });
                    return {
                      ...group,
                      recentPostCount: recentPosts.length,
                      lastActivity: posts.length > 0 ? posts[0]?.createdAt : null
                    };
                  } catch (err) {
                    console.error(`Error fetching posts for group ${group.id}:`, err);
                    return { ...group, recentPostCount: 0, lastActivity: null };
                  }
                })
              );
              setGroupsWithActivity(groupsActivity.filter(g => g.recentPostCount > 0));
            }
          } catch (err) {
            console.error('Error fetching additional data:', err);
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }

    fetchDashboardData();
  }, [isAdmin, isActiveMember]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ProfileCompletionBanner />
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {userProfile?.displayName || currentUser?.displayName || 'admin'}!
        </h1>
        <p className="mt-2 text-gray-600">
          Here's what's happening in your professional network.
        </p>
        {error && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <p className="text-yellow-800">
              Some data may not be available: {error}
            </p>
          </div>
        )}
      </div>

      {/* Admin Controls */}
      {isAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-blue-900 mb-4">Admin Controls</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Link
              to="/admin/news-management"
              className="flex flex-col items-center p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <svg className="w-6 h-6 text-blue-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              <span className="text-xs font-medium text-blue-900">News</span>
            </Link>

            <Link
              to="/admin/groups"
              className="flex flex-col items-center p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <svg className="w-6 h-6 text-blue-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="text-xs font-medium text-blue-900">Groups</span>
            </Link>

            <Link
              to="/admin/seed-data"
              className="flex flex-col items-center p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <svg className="w-6 h-6 text-blue-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
              </svg>
              <span className="text-xs font-medium text-blue-900">Data</span>
            </Link>

            <Link
              to="/admin/user-management"
              className="flex flex-col items-center p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <svg className="w-6 h-6 text-blue-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              <span className="text-xs font-medium text-blue-900">Users</span>
            </Link>

            <Link
              to="/admin/migrate-events"
              className="flex flex-col items-center p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <svg className="w-6 h-6 text-blue-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-xs font-medium text-blue-900">Events</span>
            </Link>

            <Link
              to="/admin/testing"
              className="flex flex-col items-center p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <svg className="w-6 h-6 text-blue-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span className="text-xs font-medium text-blue-900">Testing</span>
            </Link>
          </div>
        </div>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Membership Status */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isActiveMember() ? 'bg-green-100' : 'bg-yellow-100'
                }`}>
                  <svg className={`w-5 h-5 ${
                    isActiveMember() ? 'text-green-600' : 'text-yellow-600'
                  }`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Membership Status
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {userProfile?.membershipTier || (isActiveMember() ? membership?.tier || 'Active' : 'Pending')}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="text-sm">
              {isActiveMember() ? (
                <span className="text-green-600">
                  Active until {toJsDate(membership?.endDate)?.toLocaleDateString()}
                </span>
              ) : (
                <Link to="/register" className="font-medium text-apn-blue hover:text-apn-dark-blue">
                  View membership options
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Profile Status */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  userProfile?.status === 'approved' ? 'bg-green-100' : 'bg-yellow-100'
                }`}>
                  <svg className={`w-5 h-5 ${
                    userProfile?.status === 'approved' ? 'text-green-600' : 'text-yellow-600'
                  }`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Profile Status
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {userProfile?.status === 'approved' ? 'Approved' : 'Pending Review'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="text-sm">
              <Link to="/profile" className="font-medium text-apn-blue hover:text-apn-dark-blue">
                View profile
              </Link>
            </div>
          </div>
        </div>

        {/* Network Stats */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Network Activity
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    Active
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="text-sm">
              <Link to="/directory" className="font-medium text-apn-blue hover:text-apn-dark-blue">
                Browse directory
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Link
              to="/jobs/create"
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-8 h-8 text-apn-blue mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="text-sm font-medium text-gray-900">Post Job</span>
            </Link>

            <Link
              to="/projects/create"
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-8 h-8 text-apn-blue mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span className="text-sm font-medium text-gray-900">Post Project</span>
            </Link>

            {isAdmin && (
              <Link
                to="/admin/events/create"
                className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <svg className="w-8 h-8 text-green-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium text-gray-900">Post Event</span>
              </Link>
            )}

            {isAdmin && (
              <Link
                to="/admin/news-management"
                className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <svg className="w-8 h-8 text-purple-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
                <span className="text-sm font-medium text-gray-900">Post News</span>
              </Link>
            )}

            <Link
              to="/events"
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-8 h-8 text-apn-blue mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium text-gray-900">View Events</span>
            </Link>

            <Link
              to="/groups"
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-8 h-8 text-apn-blue mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-sm font-medium text-gray-900">Groups</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Jobs */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Recent Jobs
              </h3>
              <Link to="/jobs" className="text-sm font-medium text-apn-blue hover:text-apn-dark-blue">
                View all
              </Link>
            </div>
            <div className="space-y-3">
              {recentJobs.length > 0 ? (
                recentJobs.map((job) => (
                  <Link key={job.id} to={`/jobs/${job.id}`} className="block border-l-4 border-apn-blue pl-4 hover:bg-gray-50 rounded-r-md p-2 transition-colors">
                    <h4 className="text-sm font-medium text-gray-900">{job.title}</h4>
                    <p className="text-sm text-gray-600">{job.company}</p>
                    <p className="text-xs text-gray-500">{job.location}</p>
                  </Link>
                ))
              ) : (
                <p className="text-sm text-gray-500">No recent jobs available.</p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Projects */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Recent Projects
              </h3>
              <Link to="/projects" className="text-sm font-medium text-apn-blue hover:text-apn-dark-blue">
                View all
              </Link>
            </div>
            <div className="space-y-3">
              {recentProjects.length > 0 ? (
                recentProjects.map((project) => (
                  <Link key={project.id} to={`/projects/${project.id}`} className="block border-l-4 border-green-500 pl-4 hover:bg-gray-50 rounded-r-md p-2 transition-colors">
                    <h4 className="text-sm font-medium text-gray-900">{project.title}</h4>
                    <p className="text-sm text-gray-600">{project.budgetRange}</p>
                    <p className="text-xs text-gray-500">{project.timeline}</p>
                  </Link>
                ))
              ) : (
                <p className="text-sm text-gray-500">No recent projects available.</p>
              )}
            </div>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Upcoming Events
              </h3>
              <Link to="/events" className="text-sm font-medium text-apn-blue hover:text-apn-dark-blue">
                View all
              </Link>
            </div>
            <div className="space-y-3">
              {upcomingEvents.length > 0 ? (
                upcomingEvents.map((event) => (
                  <Link key={event.id} to={`/events/${event.id}`} className="block border-l-4 border-purple-500 pl-4 hover:bg-gray-50 rounded-r-md p-2 transition-colors">
                    <h4 className="text-sm font-medium text-gray-900">{event.title}</h4>
                    <p className="text-sm text-gray-600">{event.location}</p>
                    <p className="text-xs text-gray-500">
                      {event.startDate?.toDate?.()?.toLocaleDateString() || 'Date TBD'}
                    </p>
                  </Link>
                ))
              ) : (
                <p className="text-sm text-gray-500">No upcoming events.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Admin-specific sections */}
      {isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Groups with Recent Activity */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Groups with Recent Activity
                </h3>
                <Link to="/admin/groups" className="text-sm font-medium text-apn-blue hover:text-apn-dark-blue">
                  Manage all
                </Link>
              </div>
              <div className="space-y-3">
                {groupsWithActivity.length > 0 ? (
                  groupsWithActivity.map((group) => (
                    <Link key={group.id} to={`/groups/${group.id}`} className="block border-l-4 border-orange-500 pl-4 hover:bg-gray-50 rounded-r-md p-2 transition-colors">
                      <h4 className="text-sm font-medium text-gray-900">{group.name}</h4>
                      <p className="text-sm text-gray-600">{group.recentPostCount} new posts</p>
                      <p className="text-xs text-gray-500">{group.memberCount} members</p>
                    </Link>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No recent group activity.</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent News Articles */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Recent News Articles
                </h3>
                <Link to="/admin/news-management" className="text-sm font-medium text-apn-blue hover:text-apn-dark-blue">
                  Manage news
                </Link>
              </div>
              <div className="space-y-3">
                {recentArticles.length > 0 ? (
                  recentArticles.map((article) => (
                    <Link key={article.id} to={`/news/${article.id}`} className="block border-l-4 border-red-500 pl-4 hover:bg-gray-50 rounded-r-md p-2 transition-colors">
                      <h4 className="text-sm font-medium text-gray-900">{article.title}</h4>
                      <p className="text-sm text-gray-600">{article.category}</p>
                      <p className="text-xs text-gray-500">
                        {article.publishedAt?.toDate?.()?.toLocaleDateString() || article.date}
                      </p>
                    </Link>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No recent articles.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
